import { all, call, put, retry, takeEvery, takeLatest } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import { API_CONFIGURATIONS, Api } from '../services/services';
import { getAccessToken } from './authentication.sagas';
import {
    AddInspectionCommentActionType,
    ApplicationActionType,
    DeleteViolationActionType,
    AddInspectionViolationActionType,
    Form,
    FormTypeConfiguration,
    Inspection,
    InspectionUpdateActionType,
    ViolationList,
    Violation,
    CommentList,
    Comment,
    AddInspectionFormAction,
} from '../modules/application/types';
import { inspectionActions, siteActions } from '../actions';
import inspectionDefaultEditForm from '../../views/InspectionDetailTabs/Tabs/InspectionData/inspectionDefaultEditForm.json';
import addInspectionDefaultForm from '../../views/LicenseDetailTabs/Tabs/AddInspectionDialog/addInspectionDefaultForm.json';

function* getInspectionDetailById(action: ApplicationActionType) {
    const id: string = action.payload.id;
    let inspection: Inspection;
    let editForm: Form = inspectionDefaultEditForm;
    let inspectionType: FormTypeConfiguration;

    try {
        yield put(inspectionActions.getInspectionDetail.pending);
        const inspectionToken: string = yield call(getAccessToken, 'inspections.read');
        inspection = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInspectionDetail,
            inspectionToken,
            id,
        );
        const inspectionTypesToken: string = yield call(getAccessToken, 'inspectionTypes.read');
        inspectionType = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInspectionTypeById,
            inspectionTypesToken,
            inspection.type.id,
        );
        if (inspectionType?.views?.edit?.formId) {
            try {
                const formsToken: string = yield call(getAccessToken, 'forms.read');
                editForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    formsToken,
                    inspectionType.views.edit.formId,
                );
            } catch (error) {
                yield put(
                    siteActions.setError({
                        isError: false,
                        errorCode: 0,
                        message: 'Something went wrong. Please contact IT support.',
                    }),
                );
            }
        }
        yield put(inspectionActions.getInspectionDetail.success(inspection, editForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.getInspectionDetail.error(error));
    }
}

function* updateInspection(action: InspectionUpdateActionType) {
    const data: Inspection = action.payload.data;
    const inspectionId: string = action.payload.inspectionId;
    try {
        yield put(inspectionActions.updateInspection.pending);
        const token: string = yield call(getAccessToken, 'inspections.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updateInspection,
            token,
            data,
            inspectionId,
        );
        yield put(inspectionActions.getInspectionDetailsBegin(inspectionId));
        yield put(inspectionActions.updateInspection.success(action.payload.data));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Information has been saved.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.updateInspection.error(error));
    }
}

function* addInspectionComment(action: AddInspectionCommentActionType) {
    const data: string = action.payload.data;
    const inspectionId: string = action.payload.inspectionId;
    try {
        yield put(inspectionActions.addInspectionComment.pending);
        const token: string = yield call(getAccessToken, 'inspections.write');
        const result: Comment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addInspectionComment,
            token,
            data,
            inspectionId,
        );

        yield put(inspectionActions.addInspectionComment.success(result));
        yield put(
            siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Inspection comment has been added.' }),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.addInspectionComment.error(error));
    }
}

function* getInspectionCommentList(action: ApplicationActionType) {
    const inspectionId: string = action.payload.id;
    try {
        yield put(inspectionActions.getInspectionCommentList.pending);
        const token: string = yield call(getAccessToken, 'inspections.read');
        const response: CommentList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInspectionCommentList,
            token,
            inspectionId,
            action.paginationData,
        );
        yield put(
            inspectionActions.getInspectionCommentList.success(
                response.length > 0 ? response : [],
                action.paginationData,
            ),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.getInspectionCommentList.error(error));
    }
}

function* getViolationListAssociatedWithInspection(action: ApplicationActionType) {
    const inspectionId: string = action.payload.id;
    try {
        const token: string = yield call(getAccessToken, 'violations.read');
        const response: ViolationList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getViolationsAssociatedWithInspection,
            token,
            inspectionId,
            action?.paginationData,
        );
        yield put(inspectionActions.getViolationListAssociatedWithInspection.success(response, action?.paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.getViolationListAssociatedWithInspection.error(error));
    }
}

function* addViolationAssociatedWithInspection(action: AddInspectionViolationActionType) {
    const data: ViolationList = action.payload.data;
    try {
        yield put(inspectionActions.addViolationAssociatedWithInspection.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        const result: Violation[] = [];
        for (let index = 0; index < data.length; index++) {
            const element = data[index];
            const violation: Violation = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.addViolation,
                token,
                element,
            );
            violation.id && result.push(violation);
        }
        yield put(inspectionActions.addViolationAssociatedWithInspection.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Violation has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.addViolationAssociatedWithInspection.error(error));
    }
}

function* deleteViolationAssociatedWithInspection(action: DeleteViolationActionType) {
    const violationId: string = action.payload.violationId;
    try {
        yield put(inspectionActions.deleteViolationAssociatedWithInspection.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteViolation,
            token,
            violationId,
        );
        yield put(inspectionActions.deleteViolationAssociatedWithInspection.success(violationId));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Violation has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.deleteViolationAssociatedWithInspection.error(error));
    }
}

function* getAddInspectionFormById(action: AddInspectionFormAction) {
    const formId: string | undefined = action.payload.id;
    let addInspectionForm: Form = addInspectionDefaultForm;
    try {
        yield put(inspectionActions.getAddInspectionFormById.pending);
        const formsToken: string = yield call(getAccessToken, 'forms.read');
        if (!!formId) {
            addInspectionForm = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getForm,
                formsToken,
                formId,
            );
        }
        yield put(inspectionActions.getAddInspectionFormById.success(addInspectionForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inspectionActions.getAddInspectionFormById.error(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_INSPECTION_DETAIL_BY_ID_BEGIN, getInspectionDetailById),
        takeLatest(ACTIONS.UPDATE_INSPECTION_BEGIN, updateInspection),
        takeLatest(ACTIONS.ADD_INSPECTION_COMMENT_BEGIN, addInspectionComment),
        takeLatest(ACTIONS.INSPECTION_COMMENT_LIST_BEGIN, getInspectionCommentList),
        takeEvery(ACTIONS.GET_INSPECTION_VIOLATION_LIST_BEGIN, getViolationListAssociatedWithInspection),
        takeEvery(ACTIONS.DELETE_INSPECTION_VIOLATION_BEGIN, deleteViolationAssociatedWithInspection),
        takeEvery(ACTIONS.ADD_INSPECTION_VIOLATION_BEGIN, addViolationAssociatedWithInspection),
        takeEvery(ACTIONS.GET_ADD_INSPECTION_FORM_BY_ID_BEGIN, getAddInspectionFormById),
    ]);
}
